import React from "react";
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import {
  deleteFieldOfSelectedFile,
  setClipboardField,
  setReduxSelectedFile,
  updateFileInSignRequest,
} from "redux/appSlice";
import { FileField } from "models/interfaces";
import strings from "global/constants/StringConstants";
import { getNextAvailableId } from "../../SignRequestHelper";

/**
 * "More option" popover, anchored to the three-dots of the field options bar.
 * Mirrors the desktop right-click ContextMenu field actions:
 *   Duplicate · Cut · Copy · Delete
 * All actions dispatch the same redux updates as desktop so behaviour matches.
 */
const MobileFieldMoreMenu = ({
  anchorEl,
  field,
  onClose,
}: {
  anchorEl: HTMLElement | null;
  field: FileField | null;
  onClose: () => void;
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const dispatch = useAppDispatch();
  const { selectedFile } = useAppSelector((s: RootState) => s.app);

  const close = () => onClose();

  const handleDuplicate = () => {
    if (!field || !selectedFile) return;
    const newField: any = structuredClone(field);
    let px = newField.fieldPageX + 3;
    let py = newField.fieldPageY + 3;
    if (py + newField.fieldPageHeight >= 98) py -= 6;
    if (px + newField.fieldPageWidth >= 98) px -= 6;
    newField.fieldPageX = px;
    newField.fieldPageY = py;
    newField.id = getNextAvailableId(selectedFile);
    if (newField.type === strings.radioButton) {
      newField.value = "false";
      const siblingCount = (selectedFile.fields || []).filter(
        (f: FileField) =>
          f.type === strings.radioButton && f.groupId === newField.groupId,
      ).length;
      newField.label = `Option ${siblingCount + 1}`;
    }
    const updatedFile = {
      ...selectedFile,
      fields: [...(selectedFile.fields || []), newField],
    };
    dispatch(setReduxSelectedFile(updatedFile));
    dispatch(updateFileInSignRequest(updatedFile));
    close();
  };

  const handleCopy = () => {
    if (!field) return;
    dispatch(setClipboardField(field));
    close();
  };

  const handleCut = () => {
    if (!field) return;
    dispatch(setClipboardField(field));
    dispatch(deleteFieldOfSelectedFile(field));
    close();
  };

  const handleDelete = () => {
    if (!field) return;
    dispatch(deleteFieldOfSelectedFile(field));
    close();
  };

  return (
    <Popover
      open={Boolean(anchorEl) && !!field}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      PaperProps={{
        sx: {
          mt: -1,
          borderRadius: "12px",
          minWidth: 220,
          bgcolor: m.surface.white,
          boxShadow: "0px 4px 5.95px rgba(33,21,71,0.1)",
        },
      }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        {/* Title — Body 1 semi bold, base color-900 */}
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 600,
            lineHeight: "normal",
            color: m.base[900],
            mb: 0.25,
          }}
        >
          More option
        </Typography>

        <MenuList
          disablePadding
          aria-label="More field options"
          sx={{
            // Rows — Body 2 (Montserrat Medium 14/20). Divider inset matches the
            // Figma card (line spans the 16px-padded content width).
            "& .MuiMenuItem-root": { px: 0, py: 0.5 },
            "& .MuiListItemText-primary": {
              fontSize: "0.875rem",
              fontWeight: 500,
              lineHeight: "20px",
              color: m.base[900],
            },
            "& .MuiListItemIcon-root": { color: m.base[900], minWidth: 32 },
          }}
        >
          <MenuItem onClick={handleDuplicate}>
            <ListItemIcon>
              <FileCopyOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Duplicate</ListItemText>
          </MenuItem>
          <Divider sx={{ borderColor: m.grey[50], m: 0 }} />

          <MenuItem onClick={handleCut}>
            <ListItemIcon>
              <ContentCutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cut</ListItemText>
          </MenuItem>
          <Divider sx={{ borderColor: m.grey[50], m: 0 }} />

          <MenuItem onClick={handleCopy}>
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy</ListItemText>
          </MenuItem>
          <Divider sx={{ borderColor: m.grey[50], m: 0 }} />

          <MenuItem
            onClick={handleDelete}
            sx={{
              color: theme.palette.error.main,
              "& .MuiListItemIcon-root": { color: theme.palette.error.main },
              "& .MuiListItemText-primary": { color: theme.palette.error.main },
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </MenuList>
      </Box>
    </Popover>
  );
};

export default MobileFieldMoreMenu;
